import { Element } from "@xmpp/xml"
import { Tacho } from "@/reducers/tachosSlice"
import { parseCrewMembers, parseLegalCounters } from "./legalCountersParser"
import PubSubEventParser from "./PubSubEventParser"
import { readingFromElement } from "./tachoReader"

class TachoParser {
  static readonly NAMESPACE = "urn:slickshift:tacho:0"

  static parse(stanza: Element): Tacho | undefined {
    const tacho = stanza?.getChild("tacho", this.NAMESPACE)

    if (!tacho) return

    const deviceId = tacho.getAttr("device_id")
    const remainingSecondsToday = parseInt(
      tacho.getAttr("remaining_seconds_today") ?? "0",
      10,
    )
    const usedSecondsToday = parseInt(
      tacho.getAttr("used_seconds_today") ?? "0",
      10,
    )
    const remainingMiddayBreakSeconds = tacho.getAttr(
      "remaining_midday_break_seconds",
    )
      ? parseInt(tacho.getAttr("remaining_midday_break_seconds")!, 10)
      : null
    const remainingBeforeDayStartBreakSeconds = tacho.getAttr(
      "remaining_before_day_start_break_seconds",
    )
      ? parseInt(tacho.getAttr("remaining_before_day_start_break_seconds")!, 10)
      : null
    const remainingAfterDayEndBreakSeconds = tacho.getAttr(
      "remaining_after_day_end_break_seconds",
    )
      ? parseInt(tacho.getAttr("remaining_after_day_end_break_seconds")!, 10)
      : null
    const remainingSecondsThisWeek = tacho.getAttr(
      "remaining_seconds_this_week",
    )
      ? parseInt(tacho.getAttr("remaining_seconds_this_week")!, 10)
      : null
    const remainingSecondsOfUninterruptedDriving = tacho.getAttr(
      "remaining_seconds_of_uninterrupted_driving",
    )
      ? parseInt(
          tacho.getAttr("remaining_seconds_of_uninterrupted_driving")!,
          10,
        )
      : null
    const remainingWeeklyRestSeconds = tacho.getAttr(
      "remaining_weekly_rest_seconds",
    )
      ? parseInt(tacho.getAttr("remaining_weekly_rest_seconds")!, 10)
      : null
    const remainingShortenedDailyRests = tacho.getAttr(
      "remaining_shortened_daily_rests",
    )
      ? parseInt(tacho.getAttr("remaining_shortened_daily_rests")!, 10)
      : null
    const remainingSecondsTwoWeeks = tacho.getAttr(
      "remaining_seconds_two_weeks",
    )
      ? parseInt(tacho.getAttr("remaining_seconds_two_weeks")!, 10)
      : null

    const currentActivity = tacho.getAttr("current_activity")
    const timestamp = tacho.getAttr("timestamp")

    // To polish locale
    const dayEndTimestamp = tacho.getAttr("day_end_timestamp")
      ? new Date(tacho.getAttr("day_end_timestamp")!).toLocaleTimeString(
          "pl-PL",
        )
      : null

    const remainingDailyExtensions = tacho.getAttr("remaining_daily_extensions")
      ? parseInt(tacho.getAttr("remaining_daily_extensions")!, 10)
      : null

    const doubleCrew = tacho.getAttr("double_crew") === "true"
    const driverName = tacho.getAttr("driver_name") || null
    const legalCounters = parseLegalCounters(tacho.getAttr("legal_counters"))
    const crew = parseCrewMembers(tacho.getAttr("crew"))

    return {
      jid: stanza.getAttr("id"),
      deviceId,
      remainingSecondsToday,
      usedSecondsToday,
      remainingMiddayBreakSeconds,
      remainingBeforeDayStartBreakSeconds,
      remainingAfterDayEndBreakSeconds,
      remainingSecondsThisWeek,
      remainingSecondsOfUninterruptedDriving,
      remainingWeeklyRestSeconds,
      remainingShortenedDailyRests,
      remainingSecondsTwoWeeks,
      currentActivity,
      timestamp,
      dayEndTimestamp,
      remainingDailyExtensions,
      doubleCrew,
      driverName,
      legalCounters,
      ...(crew ? { crew } : {}),
      ...readingFromElement(tacho),
    }
  }
}

export const TachoPubSubParser = new PubSubEventParser<Tacho>(
  TachoParser.parse,
  TachoParser.NAMESPACE,
)
